import React from 'react'
import { Sparkles, X } from 'lucide-react'
import EpochMonthService from '../../../../services/EpochMonth.service'
import { requestOrbitIndiePanelOpen } from '../../../../services/orbit/orbitPageAssistantBridge'

export type HistoryMetricKey = 'grossMargin' | 'salesGrowth' | 'markdowns' | 'turn' | 'freshness'

export interface MonthlyMetricPoint {
  /** This month's actual (GM%, growth%, markdown%, turn×, freshness%). */
  actual: number
  /** Vertical benchmark for the metric. */
  benchmark: number
  /** Points earned this month (0…max). */
  pts: number
  /** Maximum weight for the metric. */
  max: number
}

export interface MonthlyScorePoint {
  /** Anchor epoch month this trailing-12 score ends on. */
  epochMonth: number
  /** Short month label, e.g. "Sep" */
  label: string
  /** Calendar year of the anchor month. */
  year: number
  score: number
  /** Band label for the hover detail box, e.g. "Good". */
  band: string
  /** Per-metric actuals/benchmarks/points for the By-metric view + hover line. */
  metrics: Partial<Record<HistoryMetricKey, MonthlyMetricPoint>>
}

export interface ScoreHistorySectionProps {
  isOpen: boolean
  onClose: () => void
  currentScore: number
  /** What the benchmarks are, e.g. "the top 25% of Outdoor retailers". */
  benchmarkBasis: string
  clientLabel: string
  throughLabel: string
  /**
   * Trailing-12 monthly score series, oldest → newest. HealthCashFlowSection builds it
   * from one /health-score-history call, scoring each month with the card's own pipeline
   * against today's benchmarks. Null/empty → the band/axis scaffold with an empty state.
   */
  series?: MonthlyScorePoint[] | null
  /**
   * SPS-5568: load status so the empty state can tell a genuine "no data yet" apart from
   * an actual fetch failure. 'error' shows the retry copy; 'loading' shows a spinner-free
   * loading line; 'loaded'/'idle' with no series shows the "feed not connected" copy.
   * Defaults to 'loaded' so existing callers keep the no-data behavior.
   */
  status?: 'idle' | 'loading' | 'loaded' | 'error'
}

const METRIC_PROMPT_LABELS: Record<HistoryMetricKey, string> = {
  grossMargin: 'Gross margin',
  salesGrowth: 'Sales growth',
  markdowns: 'Markdowns',
  turn: 'Annual turn',
  freshness: 'Inventory freshness',
}

function formatHistoryMetric(key: HistoryMetricKey, value: number): string {
  if (key === 'turn') return `${value.toFixed(2)}×`
  if (key === 'salesGrowth') return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}%`
  return `${value.toFixed(1)}%`
}

// Ask Indie prompt for the history modal: every monthly score, then each metric month by
// month (actual and points), so Indie can explain the trend.
function buildHistoryPrompt(points: MonthlyScorePoint[], clientLabel: string, benchmarkBasis: string, throughLabel: string): string {
  const scores = points.map((p) => `${p.label} ${p.year}: ${p.score} (${p.band})`).join('; ')
  const latest = points[points.length - 1]
  const metricLines = (Object.keys(METRIC_PROMPT_LABELS) as HistoryMetricKey[])
    .filter((key) => latest?.metrics[key])
    .map((key) => {
      const months = points
        .filter((p) => p.metrics[key])
        .map((p) => `${p.label} ${formatHistoryMetric(key, p.metrics[key]!.actual)} (${p.metrics[key]!.pts.toFixed(1)}/${p.metrics[key]!.max})`)
        .join(', ')
      return `${METRIC_PROMPT_LABELS[key]} — benchmark ${formatHistoryMetric(key, latest.metrics[key]!.benchmark)}: ${months}`
    })
  return `Health Score history${clientLabel ? ` for ${clientLabel}` : ''}, ${points.length} months through ${throughLabel}. `
    + `Each point is the score for the 12 months ending that month, against today's benchmarks (${benchmarkBasis}). `
    + `Monthly scores: ${scores}. `
    + `By metric (actual and points earned each month): ${metricLines.join('. ')}. `
    + `Explain the trend: which metrics drove the changes, and what to focus on next.`
}

// SPS-5568: distinct copy for a fetch failure vs a genuine empty feed.
const NO_DATA_COPY = 'Score history will appear here once the monthly feed is connected.'
const ERROR_COPY =
  "We couldn't load your score history right now. Try reopening this, or contact support if it keeps happening."
const LOADING_COPY = 'Loading your score history…'

// Chart geometry mirrors History.dc.html (viewBox 0 0 720 232).
const CHART_TOP = 16
const CHART_BOTTOM = 200
const CHART_LEFT = 60
const CHART_RIGHT = 690

const SCORE_BANDS = [
  { label: 'EXCELLENT', y: 16, h: 27.6, fill: '#ECFDF3', text: '#027A48' },
  { label: 'GOOD', y: 43.6, h: 27.6, fill: '#F5FBEA', text: '#4B7A12' },
  { label: 'AVERAGE', y: 71.2, h: 27.6, fill: '#FEFBE8', text: '#A16207' },
  { label: 'FAIR', y: 98.8, h: 27.6, fill: '#FEF6EE', text: '#B54708' },
  { label: 'POOR', y: 126.4, h: 73.6, fill: '#FEF3F2', text: '#B42318' },
]

// Per-metric line config for the "By metric" view + the hover breakdown line.
const HISTORY_METRIC_CONFIG: { key: HistoryMetricKey; abbr: string; color: string }[] = [
  { key: 'grossMargin', abbr: 'GM', color: '#5558D4' },
  { key: 'salesGrowth', abbr: 'growth', color: '#F79009' },
  { key: 'markdowns', abbr: 'MD', color: '#17B26A' },
  { key: 'turn', abbr: 'turn', color: '#175CD3' },
  { key: 'freshness', abbr: 'fresh', color: '#B54708' },
]

const BAND_TEXT: Record<string, string> = {
  Excellent: '#027A48',
  Good: '#4B7A12',
  Average: '#A16207',
  Fair: '#B54708',
  Poor: '#B42318',
}

function scoreToY(score: number): number {
  const clamped = Math.max(0, Math.min(100, score))
  return CHART_BOTTOM - (clamped / 100) * (CHART_BOTTOM - CHART_TOP)
}

function monthX(index: number, count: number): number {
  if (count <= 1) return CHART_RIGHT
  return CHART_LEFT + (index * (CHART_RIGHT - CHART_LEFT)) / (count - 1)
}

// Mockup number formats: actual carries its unit (51.4% / −6.8% / 3.40×), the
// benchmark is bare (46.4 / +6.0 / 3.20 / 58).
function fmtMetricNum(key: HistoryMetricKey, value: number, isBench: boolean): string {
  if (key === 'turn') return `${value.toFixed(2)}${isBench ? '' : '×'}`
  if (key === 'salesGrowth') return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}${isBench ? '' : '%'}`
  if (key === 'freshness') return `${isBench ? String(Math.round(value)) : value.toFixed(1)}${isBench ? '' : '%'}`
  return `${value.toFixed(1)}${isBench ? '' : '%'}`
}

// A metric's monthly contribution as a 0–100 share of its weight, so every driver
// shares the score axis in the "By metric" view.
function metricContributionPct(m: MonthlyMetricPoint): number {
  if (!m.max) return 0
  return Math.max(0, Math.min(100, (m.pts / m.max) * 100))
}

export function ScoreHistorySection({
  isOpen,
  onClose,
  currentScore,
  benchmarkBasis,
  clientLabel,
  throughLabel,
  series = null,
  status = 'loaded',
}: ScoreHistorySectionProps) {
  const [view, setView] = React.useState<'total' | 'metric'>('total')
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null)

  if (!isOpen) return null

  const hasSeries = Array.isArray(series) && series.length > 0
  const isError = status === 'error'
  // Only treat as "loading" while there's nothing to show yet; a refetch over an existing
  // series keeps the chart visible rather than flashing the overlay.
  const isLoading = status === 'loading' && !hasSeries
  const emptyStateCopy = isError ? ERROR_COPY : isLoading ? LOADING_COPY : NO_DATA_COPY
  const points = hasSeries ? series! : []
  const count = points.length

  // Month labels: from the series when present, else a trailing-12 scaffold. A point is
  // labelled by the last closed month in its window (snapshot month − 1).
  const endEpoch = EpochMonthService.getTodayEpochMonth() - 1
  const scaffoldLabels = Array.from({ length: 12 }, (_, i) => EpochMonthService.epochMonthToMMM(endEpoch - (11 - i)))
  const xLabels = hasSeries ? points.map((p) => p.label) : scaffoldLabels
  const labelCount = hasSeries ? count : 12

  // Only flag a "low" point when the series actually varies.
  const isFlat = hasSeries && points.every((p) => p.score === points[0].score)
  const lowIndex = hasSeries && !isFlat
    ? points.reduce((min, p, i, arr) => (p.score < arr[min].score ? i : min), 0)
    : -1

  // Hover (or latest as the resting state) drives the detail box + guide line.
  const activeIndex = hoverIndex != null && hoverIndex >= 0 && hoverIndex < count
    ? hoverIndex
    : hasSeries ? count - 1 : -1
  const activePt = activeIndex >= 0 ? points[activeIndex] : null
  const prevPt = activeIndex > 0 ? points[activeIndex - 1] : null
  const activeDelta = activePt && prevPt ? activePt.score - prevPt.score : null

  const detailLine = activePt
    ? HISTORY_METRIC_CONFIG.map((c) => {
        const m = activePt.metrics[c.key]
        if (!m) return null
        return `${c.abbr} ${fmtMetricNum(c.key, m.actual, false)} (bm ${fmtMetricNum(c.key, m.benchmark, true)})`
      })
        .filter(Boolean)
        .join(' · ')
    : ''

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="fixed inset-0" onClick={onClose} aria-hidden="true" />

      <div
        className="relative z-10 flex max-h-full w-full max-w-[780px] flex-col overflow-hidden rounded-xl border border-[#EAECF0] bg-white shadow-2xl animate-in zoom-in-95 duration-200"
        style={{ fontFamily: "'Inter', system-ui, sans-serif", color: '#101828' }}
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-[#EAECF0] px-6 pb-4 pt-5">
          <div>
            <div className="text-[10px] font-bold tracking-[0.09em] text-[#5558D4]">RETAILORBIT HEALTH SCORE™</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[19px] font-bold">Score history</span>
              {hasSeries && (
                <button
                  type="button"
                  onClick={() => requestOrbitIndiePanelOpen({
                    source: 'health-score-history',
                    prompt: buildHistoryPrompt(points, clientLabel, benchmarkBasis, throughLabel),
                    submit: true,
                  })}
                  className="inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-[#534AB7] hover:underline"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Ask Indie
                </button>
              )}
            </div>
            <div className="mt-0.5 text-xs text-[#667085]">
              {clientLabel ? `${clientLabel} · ` : ''}12 months through {throughLabel}
            </div>
          </div>
          <span className="flex-grow" />
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setView('total')}
              className="rounded-md px-2.5 py-[5px] text-[11px] font-semibold"
              style={
                view === 'total'
                  ? { background: '#5558D4', color: '#FFFFFF' }
                  : { background: '#FFFFFF', border: '1px solid #EAECF0', color: '#475467' }
              }
            >
              Total score
            </button>
            <button
              type="button"
              onClick={() => setView('metric')}
              className="rounded-md px-2.5 py-[5px] text-[11px] font-medium"
              style={
                view === 'metric'
                  ? { background: '#5558D4', color: '#FFFFFF' }
                  : { background: '#FFFFFF', border: '1px solid #EAECF0', color: '#475467' }
              }
            >
              By metric
            </button>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="ml-1.5 flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-[#EAECF0] bg-white text-[#667085] hover:bg-slate-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* By-metric legend */}
        {hasSeries && view === 'metric' && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-6 pt-3">
            {HISTORY_METRIC_CONFIG.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1.5 text-[11px] text-[#475467]">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                {c.abbr}
              </span>
            ))}
            <span className="text-[11px] text-[#98A2B3]">· each line is that metric’s monthly contribution (points ÷ max)</span>
          </div>
        )}

        {/* Chart */}
        <div className="relative px-6 pb-0.5 pt-3.5">
          <svg viewBox="0 0 720 232" style={{ width: '100%', height: 232 }} role="img" aria-label="Trailing-12 health score history">
            {SCORE_BANDS.map((band) => (
              <g key={band.label}>
                <rect x={48} y={band.y} width={652} height={band.h} fill={band.fill} />
                <text x={694} y={band.y + 11} textAnchor="end" fontFamily="Inter" fontSize="8" fontWeight="600" fill={band.text} opacity="0.75">
                  {band.label}
                </text>
              </g>
            ))}
            <line x1={48} y1={200} x2={700} y2={200} stroke="#EAECF0" strokeWidth={1} />
            <text x={40} y={19} textAnchor="end" fontFamily="Inter" fontSize="9" fill="#98A2B3">100</text>
            <text x={40} y={111} textAnchor="end" fontFamily="Inter" fontSize="9" fill="#98A2B3">50</text>
            <text x={40} y={203} textAnchor="end" fontFamily="Inter" fontSize="9" fill="#98A2B3">0</text>

            {/* Hover guide line */}
            {hasSeries && activeIndex >= 0 && (
              <line
                x1={monthX(activeIndex, count)}
                y1={CHART_TOP}
                x2={monthX(activeIndex, count)}
                y2={CHART_BOTTOM}
                stroke="#D0D5DD"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            )}

            {/* Total-score line */}
            {hasSeries && view === 'total' && (
              <>
                <polyline
                  points={points.map((p, i) => `${monthX(i, count)},${scoreToY(p.score)}`).join(' ')}
                  fill="none"
                  stroke="#5558D4"
                  strokeWidth={2.5}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {points.map((p, i) => {
                  const isLatest = i === count - 1
                  const isLow = i === lowIndex
                  const isActive = i === activeIndex
                  const cx = monthX(i, count)
                  const cy = scoreToY(p.score)
                  if (isLatest) {
                    return (
                      <g key={`pt-${i}`}>
                        <circle cx={cx} cy={cy} r={isActive ? 6.5 : 5.5} fill="#5558D4" stroke="#FFFFFF" strokeWidth={2.5} />
                        <text x={cx} y={cy - 11} textAnchor="middle" fontFamily="Inter" fontSize="11" fontWeight="700" fill="#5558D4">
                          {p.score}
                        </text>
                      </g>
                    )
                  }
                  if (isLow) {
                    return (
                      <g key={`pt-${i}`}>
                        <circle cx={cx} cy={cy} r={isActive ? 5.5 : 4.5} fill="#F04438" stroke="#FFFFFF" strokeWidth={2} />
                        <text x={cx} y={cy + 16} textAnchor="middle" fontFamily="Inter" fontSize="9" fontWeight="700" fill="#B42318">
                          {p.score} low
                        </text>
                      </g>
                    )
                  }
                  return <circle key={`pt-${i}`} cx={cx} cy={cy} r={isActive ? 5 : 3.5} fill="#FFFFFF" stroke="#5558D4" strokeWidth={2} />
                })}
              </>
            )}

            {/* By-metric lines (each driver's monthly contribution, 0–100) */}
            {hasSeries && view === 'metric' && HISTORY_METRIC_CONFIG.map((c) => {
              const pts = points
                .map((p, i) => {
                  const m = p.metrics[c.key]
                  if (!m) return null
                  return `${monthX(i, count)},${scoreToY(metricContributionPct(m))}`
                })
                .filter(Boolean) as string[]
              if (pts.length < 2) return null
              return (
                <g key={`line-${c.key}`}>
                  <polyline points={pts.join(' ')} fill="none" stroke={c.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" opacity={0.9} />
                  {activeIndex >= 0 && points[activeIndex]?.metrics[c.key] && (
                    <circle
                      cx={monthX(activeIndex, count)}
                      cy={scoreToY(metricContributionPct(points[activeIndex].metrics[c.key]!))}
                      r={3.5}
                      fill={c.color}
                      stroke="#FFFFFF"
                      strokeWidth={1.5}
                    />
                  )}
                </g>
              )
            })}

            {xLabels.map((label, i) => (
              <text key={`m-${i}`} x={monthX(i, labelCount)} y={217} textAnchor="middle" fontFamily="Inter" fontSize="9" fill="#667085">
                {label}
              </text>
            ))}
            <text x={374} y={230} textAnchor="middle" fontFamily="Inter" fontSize="8" fill="#98A2B3">
              {view === 'metric' ? 'each line is a metric’s monthly contribution to the score' : 'each point is that month’s trailing-12 score'}
            </text>

            {/* Transparent hover hit areas (one column per month) */}
            {hasSeries && points.map((_, i) => {
              const cx = monthX(i, count)
              const half = count > 1 ? (CHART_RIGHT - CHART_LEFT) / (count - 1) / 2 : (CHART_RIGHT - CHART_LEFT) / 2
              return (
                <rect
                  key={`hit-${i}`}
                  x={cx - half}
                  y={CHART_TOP}
                  width={half * 2}
                  height={CHART_BOTTOM - CHART_TOP}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoverIndex(i)}
                  onMouseLeave={() => setHoverIndex(null)}
                />
              )
            })}
          </svg>

          {!hasSeries && (
            <div className="pointer-events-none absolute inset-x-6 top-3.5 flex items-center justify-center" style={{ height: 184 }}>
              <div
                className="rounded-lg border bg-white/90 px-4 py-2.5 text-center text-xs shadow-sm"
                style={isError ? { borderColor: '#FDA29B', color: '#B42318' } : { borderColor: '#EAECF0', color: '#667085' }}
              >
                {emptyStateCopy}
                {!isError && !isLoading && Number.isFinite(currentScore) ? ` This month: ${Math.round(currentScore)}.` : ''}
              </div>
            </div>
          )}
        </div>

        {/* Footer detail + ceiling note */}
        <div className="flex gap-2.5 px-6 pb-[18px] pt-2">
          <div className="flex-grow rounded-[9px] border border-[#EAECF0] px-3 py-2.5">
            <div className="text-[9px] font-bold tracking-[0.09em] text-[#667085]">
              {hoverIndex != null ? 'HOVER A POINT' : 'LATEST MONTH'}
            </div>
            {activePt ? (
              <div className="mt-1 text-xs leading-[1.55]">
                <strong>
                  {activePt.label} {activePt.year} — {activePt.score}
                </strong>{' '}
                <span style={{ color: BAND_TEXT[activePt.band] ?? '#475467', fontWeight: 600 }}>{activePt.band}</span>
                {activeDelta != null && (
                  <span className="text-[#667085]">
                    {' '}· {activeDelta >= 0 ? '+' : '−'}
                    {Math.abs(activeDelta)} vs {prevPt!.label}
                  </span>
                )}
                {detailLine && (
                  <>
                    <br />
                    <span className="text-[#667085]">{detailLine}</span>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-1 text-xs leading-[1.55]" style={{ color: isError ? '#B42318' : '#667085' }}>
                {isError
                  ? ERROR_COPY
                  : isLoading
                    ? LOADING_COPY
                    : 'Per-month score, band, and metric breakdown will show here once the monthly feed is connected.'}
              </div>
            )}
          </div>
          <div
            className="w-[206px] shrink-0 rounded-[9px] border px-3 py-2.5"
            style={{ borderColor: '#EAECF0', borderLeft: '3px solid #5558D4', background: '#F9FAFB' }}
          >
            <div className="text-[9px] font-bold tracking-[0.09em] text-[#5558D4]">HOW TO READ THIS</div>
            <div className="mt-1 text-[11px] leading-[1.5] text-[#475467]">
              Each point is your score for the 12 months ending that month, measured against today’s benchmarks.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
